import { 
  MapPin,
  AlertTriangle,
  AlertOctagon,
  Wrench,
  Battery,
  Sun,
  Zap,
  FileText,
  LucideIcon 
} from 'lucide-react';
import { MowerModel, MowerBrand, MowerConfig, MowerDetails, MOWER_MODELS } from './mowers';

export type MapCategory = 'GOLF' | 'NORMAL';

export enum SlopeCapability {
  LIGHT = 'LIGHT',
  MEDIUM = 'MEDIUM',
  HEAVY = 'HEAVY'
}

// Add MarkerType definition
export interface MarkerTypeConfig {
  name: string;
  icon: LucideIcon;
  color: string;
  description?: string;
}

export const MARKER_TYPES: Record<string, MarkerTypeConfig> = {
  DEFAULT: {
    name: 'Location',
    icon: MapPin,
    color: 'rgb(59, 130, 246)', // blue-500
  },
  WARNING: {
    name: 'Warning',
    icon: AlertTriangle,
    color: 'rgb(245, 158, 11)', // amber-500
  },
  DANGER: {
    name: 'Danger',
    icon: AlertOctagon,
    color: 'rgb(239, 68, 68)', // red-500
  },
  MAINTENANCE: {
    name: 'Maintenance',
    icon: Wrench,
    color: 'rgb(99, 102, 241)', // indigo-500
  },
  CHARGING: {
    name: 'Charging Station',
    icon: Battery,
    color: 'rgb(16, 185, 129)', // emerald-500
  },
  SOLAR: {
    name: 'Solar Panel',
    icon: Sun,
    color: 'rgb(245, 158, 11)', // amber-500
  },
  POWER: {
    name: 'Power Source',
    icon: Zap,
    color: 'rgb(99, 102, 241)', // indigo-500
  },
  NOTE: {
    name: 'Note',
    icon: FileText,
    color: 'rgb(107, 114, 128)', // gray-500
  }
} as const;

export type MarkerType = keyof typeof MARKER_TYPES;

export interface MowerAssignment {
  model: MowerModel;
  quantity: number;
}

export interface SuperZone {
  zones: string[];  // Zone IDs
  area: number;
  mowers?: {
    automatic: boolean;
    assignments: MowerAssignment[];
  };
}

export interface Zone {
  id: string;
  name: string;
  type: string;
  paths: Array<{ lat: number; lng: number }>;
  color: string;
  area: number;
  allowsMowers: boolean;
  mowers?: {
    automatic: boolean;
    assignments: Array<{
      model: MowerModel;
      quantity: number;
    }>;
    brand?: string;
  };
  notes?: string;
  servicePlan?: string;
  isSuper?: boolean;
  superZoneId?: string;
  parentSuperZoneId?: string;
  isParent?: boolean;
}

export interface Marker {
  id: string;
  position: {
    lat: number;
    lng: number;
  };
  type: MarkerTypeKey;
  label: string;
  note?: string;
}

export type MarkerTypeKey = keyof typeof MARKER_TYPES;

export interface Measurement {
  id: string;
  name: string;
  points: google.maps.LatLngLiteral[];
  distance: number;
  color: string;
  lineWidth: number;
  unit: string;
  note?: string;
}

export interface MapTools {
  drawZone: boolean;
  addMarker: boolean;
  measure: boolean;
}

export interface SidebarCardProps {
  title: string;
  count: number;
  icon: LucideIcon;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}

export interface ZoneType {
  id: string;
  name: string;
  color: string;
  allowsMowers: boolean;
}

export const ZONE_TYPES: Record<MapCategory, Array<{
  id: string;
  name: string;
  color: string;
  allowsMowers: boolean;
  preferredMowers?: MowerModel[];
  mowingFrequency?: {
    timesPerWeek: number;
    description: string;
  };
}>> = {
  GOLF: [
    { 
      id: 'fairway', 
      name: 'Fairway', 
      color: '#4ade80', 
      allowsMowers: true,
      preferredMowers: ['HUSQVARNA_CEORA', 'KRESS_236'],
      mowingFrequency: {
        timesPerWeek: 3,
        description: 'Three times per week'
      }
    },
    { 
      id: 'rough', 
      name: 'Rough', 
      color: '#166534', 
      allowsMowers: true,
      mowingFrequency: {
        timesPerWeek: 2,
        description: 'Twice per week'
      }
    },
    { 
      id: 'tee_box', 
      name: 'Tee Box', 
      color: '#86efac', 
      allowsMowers: true,
      mowingFrequency: {
        timesPerWeek: 3,
        description: 'Three times per week'
      }
    },
    { 
      id: 'green', 
      name: 'Green', 
      color: '#22c55e', 
      allowsMowers: true,
      mowingFrequency: {
        timesPerWeek: 7,
        description: 'Daily'
      }
    },
    { 
      id: 'bunker', 
      name: 'Bunker/Sand', 
      color: '#facc15', 
      allowsMowers: false 
    },
    { 
      id: 'water', 
      name: 'Water Hazard', 
      color: '#3b82f6', 
      allowsMowers: false 
    },
    { 
      id: 'practice', 
      name: 'Practice Area', 
      color: '#14b8a6', 
      allowsMowers: true,
      mowingFrequency: {
        timesPerWeek: 3,
        description: 'Three times per week'
      }
    },
    { 
      id: 'driving_range', 
      name: 'Driving Range', 
      color: '#a855f7', 
      allowsMowers: true,
      mowingFrequency: {
        timesPerWeek: 2,
        description: 'Twice per week'
      }
    },
    { 
      id: 'cart_path', 
      name: 'Cart Path', 
      color: '#94a3b8',
      allowsMowers: false 
    },
    { 
      id: 'slope', 
      name: 'Slope Area', 
      color: '#f97316', 
      allowsMowers: true,
      preferredMowers: ['HUSQVARNA_535_AWD'],
      mowingFrequency: {
        timesPerWeek: 1,
        description: 'Once per week'
      }
    },
    { 
      id: 'stay_out', 
      name: 'Stay Out Zone', 
      color: '#dc2626', 
      allowsMowers: false 
    },
  ],
  NORMAL: [
    { 
      id: 'standard', 
      name: 'Standard Area', 
      color: '#4ade80', 
      allowsMowers: true,
      mowingFrequency: {
        timesPerWeek: 2,
        description: 'Twice per week'
      }
    },
    { 
      id: 'slope', 
      name: 'Slope Area', 
      color: '#f97316', 
      allowsMowers: true,
      preferredMowers: ['HUSQVARNA_535_AWD'],
      mowingFrequency: {
        timesPerWeek: 1,
        description: 'Once per week'
      }
    },
    { 
      id: 'stay_out', 
      name: 'Stay Out Zone', 
      color: '#dc2626', 
      allowsMowers: false 
    },
  ],
};

export interface MapDetails {
  id: string;
  name: string;
  customerId?: string;
  customerName?: string;
  type?: string;
  status: string;
  lastModified: string;
  createdAt: string;
  center?: google.maps.LatLngLiteral;
  zoom?: number;
  category?: 'NORMAL' | 'GOLF';
  zones: Zone[];  // Make this required and always an array
  markers: Marker[];  // Make this required and always an array
  measurements: Measurement[];  // Make this required and always an array
  // ... other properties
}

export const MOWER_PRICES = {
  HUSQVARNA_520_EPOS: 5000,
  HUSQVARNA_550_EPOS: 6000,
  HUSQVARNA_CEORA: 32000,
  HUSQVARNA_535_AWD: 5000,
  KRESS_236: 14000,
  KRESS_233: 9000,
  KORECHI_RANGE: 34000,
} as const;

export type MowerPrice = keyof typeof MOWER_PRICES;

export interface ZoneInteractionHandlers {
  onZoneSelect?: (zoneId: string) => void;
  onZoneEdit?: (zoneId: string) => void;
  onZoneDelete?: (zoneId: string) => void;
  selectedZoneId?: string | null;
  editingZoneId?: string | null;
}
